import * as React from 'react';
import { MessageSquare, RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@shared/ui/primitives/popover';
import { FlowChat, type FlowChatRunnerHandle } from './FlowChat';

type FlowChatPopoverProps = {
  flowId: string;
  workspaceId?: string | null;
};

const MIN_WIDTH = 320;
const MIN_HEIGHT = 320;
const VIEWPORT_MARGIN = 16;

function FlowChatPopoverInner({ flowId, workspaceId }: FlowChatPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const [runner, setRunner] = React.useState<FlowChatRunnerHandle | null>(null);
  const [size, setSize] = React.useState(() => ({ width: 520, height: 480 }));
  const resizeCleanupRef = React.useRef<(() => void) | null>(null);
  const contentId = React.useId();

  const clampDimensions = React.useCallback((next: { width: number; height: number }) => {
    if (typeof window === 'undefined') {
      return {
        width: Math.max(MIN_WIDTH, next.width),
        height: Math.max(MIN_HEIGHT, next.height),
      };
    }
    const availableWidth = Math.max(240, window.innerWidth - VIEWPORT_MARGIN * 2);
    const availableHeight = Math.max(240, window.innerHeight - VIEWPORT_MARGIN * 2);
    return {
      width: Math.min(Math.max(next.width, MIN_WIDTH), availableWidth),
      height: Math.min(Math.max(next.height, MIN_HEIGHT), availableHeight),
    };
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setSize((prev) => clampDimensions(prev));
  }, [clampDimensions, open]);

  React.useEffect(() => () => {
    resizeCleanupRef.current?.();
  }, []);

  React.useEffect(() => {
    if (!open) {
      resizeCleanupRef.current?.();
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setSize((prev) => clampDimensions(prev));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampDimensions, open]);

  const handleResizePointerDown = React.useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    resizeCleanupRef.current?.();

    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = size.width;
    const startHeight = size.height;
    const pointerId = event.pointerId;
    const target = event.currentTarget;
    target.setPointerCapture?.(pointerId);
    document.body.style.userSelect = 'none';

    const onPointerMove = (e: PointerEvent) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      setSize((prev) => {
        const next = clampDimensions({ width: startWidth + deltaX, height: startHeight + deltaY });
        if (prev.width === next.width && prev.height === next.height) {
          return prev;
        }
        return next;
      });
    };

    const finish = () => {
      target.releasePointerCapture?.(pointerId);
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      resizeCleanupRef.current = null;
    };

    resizeCleanupRef.current = finish;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  }, [clampDimensions, size.height, size.width]);

  const handleRunnerReady = React.useCallback((handle: FlowChatRunnerHandle) => {
    setRunner(handle);
  }, []);

  const handleReset = React.useCallback(() => {
    runner?.reset();
  }, [runner]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="default"
          className="fixed bottom-4 right-4 z-[75] h-11 rounded-full px-4 shadow-lg"
          aria-label="Open flow chat"
          aria-controls={contentId}
        >
          <MessageSquare className="mr-2 h-4 w-4" />
          Chat
        </Button>
      </PopoverTrigger>

      <PopoverContent
        id={contentId}
        draggable
        align="end"
        className="relative flex max-w-[calc(100vw-2rem)] min-h-[320px] min-w-[320px] flex-col overflow-hidden border bg-card p-0 shadow-xl"
        style={{ width: size.width, height: size.height }}
        closeOnOutsideClick={false}
      >
        <div
          className="flex items-center justify-between border-b px-3 py-2 text-sm"
          data-popover-drag-handle
        >
          <span className="font-medium">Flow Chat</span>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleReset}
              disabled={!runner || runner.isRunning}
              aria-label="Reset chat session"
              data-popover-no-drag
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
              Reset
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => setOpen(false)}
              aria-label="Close flow chat"
              data-popover-no-drag
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex flex-1 min-h-0 overflow-hidden p-3">
          <FlowChat flowId={flowId} workspaceId={workspaceId} onRunnerReady={handleRunnerReady} />
        </div>
        <button
          type="button"
          aria-label="Resize flow chat"
          className="absolute bottom-2 right-2 flex h-4 w-4 cursor-se-resize items-center justify-center rounded border border-border bg-muted/70 text-muted-foreground transition hover:bg-muted"
          onPointerDown={handleResizePointerDown}
          data-popover-no-drag
        >
          <span className="pointer-events-none h-2 w-2 rotate-45 border-b border-r border-current" />
        </button>
      </PopoverContent>
    </Popover>
  );
}

export const FlowChatPopover = React.memo(FlowChatPopoverInner);
